import { Row, Button, Slider } from "antd";
import React, { useEffect, useRef, useState } from "react";
import videos from "video.js";
import Icon from "@ant-design/icons";
import { SliderMarks } from "antd/lib/slider";
import VideoTimelineHooks from "@Components/activity/VideoTimelineHooks";
import { Play, Pause, Volume, NoVolume, FullScreen, Close, Speed } from "@Components/activity/VideoIcons";
import { formatDuration, useLocalStorage, useSearchParams } from "@Utils";
import "@Styles/pages/activityPage.scss";
import { ISignatureItem } from "@Models/IActivityDetails";
import SignatureChart from "@Components/activity/SignatureChart";

interface IProps {
    url?: string;
    thumbUrl?: string;
    userFullName?: string;
    title: string;
    marks?: SliderMarks;
    toggleFullScreen: () => Promise<void>;
    toggleFullScreenExit: () => void;
    onProgress?: (value: number) => void;
    onDuration?: (value: number) => void;
    options: videos.PlayerOptions;
    volumeEnabled: boolean;
    isActive?: boolean;
    isFullScreen?: boolean;
    signatureItems?: ISignatureItem[];
    isCMEPublic?: boolean;
}

export type Ref = HTMLDivElement;

const VideoTimeline = React.forwardRef<Ref, IProps>((props: IProps, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<videos.Player>(null);
    const {
        togglePlay,
        handleOnTimeUpdate,
        handleOnMetaLoaded,
        handleVideoProgress,
        handleVideoSpeed,
        handleVolume,
        toggleMute,
        setFullScreen,
        exitFullScreen,
    } = VideoTimelineHooks(videoRef, playerRef, props);

    const PlayIcon = (props) => <Icon component={Play} {...props} />;
    const PauseIcon = (props) => <Icon component={Pause} {...props} />;
    const VolumeIcon = (props) => <Icon component={Volume} {...props} />;
    const NoVolumeIcon = (props) => <Icon component={NoVolume} {...props} />;
    const FullScreenIcon = (props) => <Icon component={FullScreen} {...props} />;
    const CloseIcon = (props) => <Icon component={Close} {...props} />;
    const SpeedIcon = (props) => <Icon component={Speed} {...props} />;

    const progressPercentage = (playerRef?.current?.currentTime() / playerRef?.current?.duration()) * 100;
    const formattedCurrentTime = formatDuration(playerRef?.current?.currentTime() || 0);

    const isFullScreen = !!document?.fullscreenElement || !!(document as any).webkitCurrentFullScreenElement;

    useEffect(() => {
        if (props.isActive === false) {
            playerRef?.current?.pause();
        }
    }, [props.isActive]);

    useEffect(() => {
        document.querySelector(".scrolling-content-wrapper")?.scrollTo(0, 0);
    }, [props.url]);

    /**
     * Logic for when CME is public (e.g where video progress tracking is required to ensure user has watched the whole video)
     */

    const [searchParams] = useSearchParams();
    const urlParamActivityId = searchParams.get("activity");
    const userId = searchParams.get("user");
    const [watchedProgressPercentage, setWatchedProgressPercentage] = useLocalStorage(
        `${urlParamActivityId}-${userId}`,
        { value: 0, expiry: null }
    );
    const [noFastForwardMsg, setNoFastForwardMsg] = useState(false);
    const expiryInMinutes = 60 * 24 * 7 * 4; // one month

    const handleWatchedProgressPercentage = () => {
        if (props.isCMEPublic) {
            if (!watchedProgressPercentage) {
                setWatchedProgressPercentage(0, expiryInMinutes);
            }
            if (progressPercentage > watchedProgressPercentage?.value) {
                setWatchedProgressPercentage(progressPercentage, expiryInMinutes);
            }
        }
    };

    useEffect(() => {
        handleWatchedProgressPercentage();
    }, [playerRef?.current?.paused()]);

    // When user has switched away from tab, pause the video
    const onBlur = () => {
        playerRef?.current?.pause();
    };

    useEffect(() => {
        if (props.isCMEPublic) {
            window.addEventListener("blur", onBlur);
            // Specify how to clean up after this effect:
            return () => {
                window.removeEventListener("blur", onBlur);
            };
        } else {
            return () => {};
        }
    }, [props.isCMEPublic]);

    return (
        <div ref={ref}>
            <div key={props.url}>
                <video
                    className="video-js"
                    id={"video-player"}
                    onLoadedMetadata={handleOnMetaLoaded}
                    onTimeUpdate={handleOnTimeUpdate}
                    playsInline={true}
                    poster={props.thumbUrl}
                    ref={videoRef}
                />
            </div>
            <div className="video-controls" style={{ position: isFullScreen ? "fixed" : "relative", bottom: "0px" }}>
                <Row style={{ width: "100%" }}>
                    <Slider
                        className={`video-timeline-slider ${!handleVideoSpeed ? "no-speed-slider" : ""}`}
                        min={0}
                        max={100}
                        style={{ width: "100%" }}
                        tipFormatter={() => formattedCurrentTime}
                        defaultValue={0}
                        value={progressPercentage}
                        marks={props.marks}
                        onChange={(value: number) => {
                            if (props.isCMEPublic) {
                                if (value < watchedProgressPercentage?.value) {
                                    handleVideoProgress(value);
                                    setNoFastForwardMsg(false);
                                } else {
                                    setNoFastForwardMsg(true);
                                }
                            } else {
                                handleVideoProgress(value);
                            }
                        }}
                    />
                    {noFastForwardMsg && <div style={{ color: "red" }}>Fast forward is disabled for this video</div>}
                </Row>
                <Row style={{ width: "100%", maxHeight: "30px", justifyContent: "space-between" }}>
                    <div className={"video-play-wrapper"}>
                        <Button
                            className={"video-button video-play"}
                            shape="circle"
                            onClick={() => {
                                setNoFastForwardMsg(false);
                                togglePlay();
                            }}
                            icon={playerRef.current?.paused() ? <PlayIcon /> : <PauseIcon />}
                        />
                        <div className={"video-duration"}>
                            {formattedCurrentTime} / {formatDuration(playerRef?.current?.duration() || 0)}
                        </div>
                    </div>
                    {props.volumeEnabled && (
                        <div className={"video-volume-slider-wrapper"}>
                            <Button className={"video-button video-mute"} shape="circle" onClick={toggleMute}>
                                {!playerRef.current?.muted() ? <VolumeIcon /> : <NoVolumeIcon />}
                            </Button>
                            <Slider
                                min={0}
                                max={1}
                                className={"video-volume-slider"}
                                step={0.05}
                                defaultValue={0.75}
                                onChange={(value: number) => {
                                    handleVolume(value);
                                }}
                            />
                        </div>
                    )}
                    {!!handleVideoSpeed && !props.isCMEPublic ? (
                        <div className={"video-speed-slider-wrapper"}>
                            <SpeedIcon />
                            <Slider
                                min={1}
                                max={5}
                                className={"video-speed-slider"}
                                step={1}
                                defaultValue={1}
                                onChange={(value: number) => {
                                    handleVideoSpeed(value);
                                }}
                            />
                        </div>
                    ) : null}
                    <Button className={"video-button video-fullscreen"} shape="circle">
                        {!isFullScreen ? (
                            <FullScreenIcon onClick={setFullScreen} />
                        ) : (
                            <CloseIcon onClick={exitFullScreen} />
                        )}
                    </Button>
                </Row>
                {props.signatureItems && (
                    <>
                        <hr style={{ width: "100%", borderTop: "1px solid #053388" }} />
                        <p style={{ fontSize: "12px", color: "#1890ff", width: "100%" }}>Smoothness</p>
                        <SignatureChart
                            values={props.signatureItems}
                            lineColor={"#1890ff"}
                            fill={"l(270) 0:rgba(24,144,255,0) 1:rgba(24,144,255,0.5)"}
                        />
                    </>
                )}
            </div>
        </div>
    );
});
export default VideoTimeline;
